import { PortalApiError, portalFetch, portalJson } from './client';

/** Reads the server's machine-readable `error` code (for example "current-password-incorrect"). */
const portalErrorFrom = async (response: Response): Promise<PortalApiError> => {
  let code = `HTTP ${response.status}`;
  try {
    const data = (await response.json()) as { error?: string };
    if (data.error) code = data.error;
  } catch {
    // Not JSON; keep the status-based code.
  }
  return new PortalApiError(response.status, code);
};

export interface PortalProfile {
  customerId: number;
  username: string;
  displayName: string;
  phone: string;
  secondaryPhone: string;
  address: string;
  age: number | null;
  email: string;
}

export interface PortalTest {
  testCode: number;
  name: string;
  children: PortalTest[];
}

export interface PortalVisit {
  visitId: string;
  daySerial: number;
  visitDate: string;
  total: number;
  discountValue: number;
  paid: number;
  remaining: number;
  tests: PortalTest[];
  doctorName: string;
  resultSentOnWhatsApp: boolean;
  resultUrl: string | null;
}

interface WireProfile {
  CustomerId: number;
  Username: string;
  DisplayName: string;
  Phone?: string;
  SecondaryPhone?: string;
  Address?: string;
  Age?: number | null;
  Email?: string;
}

interface WireTest {
  TestCode: number;
  Name: string;
  Children: WireTest[];
}

interface WireVisit {
  VisitId: string;
  DaySerial: number;
  VisitDate: string;
  Total: number;
  DiscountValue: number;
  Paid: number;
  Remaining: number;
  Tests: WireTest[];
  DoctorName?: string;
  ResultSentOnWhatsApp?: boolean;
  ResultUrl?: string | null;
}

interface WireLoginResponse {
  IsSuccess: boolean;
  Token: string | null;
}

const mapTest = (test: WireTest): PortalTest => ({
  testCode: test.TestCode,
  name: test.Name,
  children: (test.Children ?? []).map(mapTest),
});

const mapVisit = (visit: WireVisit): PortalVisit => ({
  visitId: visit.VisitId,
  daySerial: visit.DaySerial,
  visitDate: visit.VisitDate,
  total: visit.Total,
  discountValue: visit.DiscountValue,
  paid: visit.Paid,
  remaining: visit.Remaining,
  tests: (visit.Tests ?? []).map(mapTest),
  doctorName: visit.DoctorName ?? '',
  resultSentOnWhatsApp: visit.ResultSentOnWhatsApp ?? false,
  resultUrl: visit.ResultUrl ?? null,
});

/** Lab runtime settings for the device must be loaded before login; failures are not fatal. */
export const bootstrapLabDevice = async (): Promise<void> => {
  try {
    await portalFetch('/api/Config/bootstrap?deviceNumber=1');
  } catch {
    // Login still runs; the server reports a clear error if the lab is unreachable.
  }
};

export const loginCustomer = async (input: {
  username: string;
  password: string;
  lang: 'ar' | 'en';
}): Promise<string> => {
  await bootstrapLabDevice();

  const response = await portalJson<WireLoginResponse>('/Secuirty/AuthenticateWithType', {
    method: 'POST',
    body: {
      username: input.username.trim(),
      password: input.password,
      type: 'Customer',
      lang: input.lang,
      IsDesktop: false,
      addons: false,
      ClientDateTime: new Date().toISOString(),
    },
  });

  if (!response.IsSuccess || !response.Token) {
    throw new PortalApiError(401, 'invalid-credentials');
  }

  return response.Token;
};

export const fetchProfile = async (token: string): Promise<PortalProfile> => {
  const profile = await portalJson<WireProfile>('/api/customer-portal/me', { token });
  return {
    customerId: profile.CustomerId,
    username: profile.Username,
    displayName: profile.DisplayName,
    phone: profile.Phone ?? '',
    secondaryPhone: profile.SecondaryPhone ?? '',
    address: profile.Address ?? '',
    age: profile.Age ?? null,
    email: profile.Email ?? '',
  };
};

export const fetchVisits = async (token: string): Promise<PortalVisit[]> => {
  const visits = await portalJson<WireVisit[]>('/api/customer-portal/me/visits', { token });
  return visits.map(mapVisit);
};

export const RESULT_LINK_PREFIX = 'https://r.elhamylabs.com/';

export const resultLinkFor = (visitId: string): string => `${RESULT_LINK_PREFIX}r/${encodeURIComponent(visitId)}`;

/**
 * Hidden check: does a PDF exist on r.elhamylabs.com for this visit? Only a definite answer (404 or not a PDF) is "no";
 * a network failure counts as "yes" so a blip does not hide a real result. The body is cancelled, only headers are read.
 */
export const resultExistsOnLink = async (visitId: string): Promise<boolean> => {
  try {
    const response = await fetch(resultLinkFor(visitId));
    const isPdf = (response.headers.get('content-type') ?? '').toLowerCase().includes('application/pdf');
    void response.body?.cancel();
    return response.ok && isPdf;
  } catch {
    return true;
  }
};

/** Fetches the PDF straight from r.elhamylabs.com so the browser can save it. Null means "use the server download instead". */
export const fetchResultFromLink = async (resultUrl: string): Promise<Blob | null> => {
  if (!resultUrl.startsWith(RESULT_LINK_PREFIX)) return null;

  try {
    const response = await fetch(resultUrl);
    if (!response.ok) return null;
    if (!(response.headers.get('content-type') ?? '').toLowerCase().includes('application/pdf')) return null;
    return await response.blob();
  } catch {
    return null;
  }
};

export interface PortalComplaint {
  complaintId: number;
  body: string;
  createdDate: string;
  isHandled: boolean;
}

interface WireComplaint {
  ComplaintId: number;
  Body: string;
  CreatedDate: string;
  IsHandled: boolean;
}

export const fetchComplaints = async (token: string): Promise<PortalComplaint[]> => {
  const complaints = await portalJson<WireComplaint[]>('/api/customer-portal/me/complaints', { token });
  return complaints.map((complaint) => ({
    complaintId: complaint.ComplaintId,
    body: complaint.Body,
    createdDate: complaint.CreatedDate,
    isHandled: complaint.IsHandled,
  }));
};

export const submitComplaint = async (
  token: string,
  input: { email: string; phone: string; body: string },
): Promise<void> => {
  const response = await portalFetch('/api/customer-portal/me/complaints', {
    method: 'POST',
    token,
    body: { Email: input.email.trim(), Phone: input.phone.trim(), Body: input.body.trim() },
  });
  if (!response.ok) {
    throw await portalErrorFrom(response);
  }
};

export const changeCustomerPassword = async (
  token: string,
  input: { currentPassword: string; newPassword: string },
): Promise<void> => {
  const response = await portalFetch('/api/customer-portal/me/password', {
    method: 'POST',
    token,
    body: { CurrentPassword: input.currentPassword, NewPassword: input.newPassword },
  });
  if (!response.ok) {
    throw await portalErrorFrom(response);
  }
};

export const deleteCustomerAccount = async (token: string, password: string): Promise<void> => {
  const response = await portalFetch('/api/customer-portal/me/account/delete', {
    method: 'POST',
    token,
    body: { Password: password },
  });
  if (!response.ok) {
    throw await portalErrorFrom(response);
  }
};
